

import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { Stack, useMediaQuery } from '@mui/system';
import { Card, Link, Button, Checkbox, MenuItem, useTheme, IconButton, Typography, CardContent } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { Contact } from 'src/services/contacts/contacts.types';
import { useGetContactCompanyLinksQuery } from 'src/services/contacts/contacts.service';
import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

import { useFeatureFlags } from 'src/sections/billing/usage/hooks/use-feature-flags';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';

import { openTaskEditorSidesheet } from 'src/store/slices/app/appSlice';
import { useAppDispatch } from 'src/store/store';
import { useCallback } from 'react';
import { useCommunicationActions, buildDialNumber } from 'src/sections/admin/integrations/communications/use-communication-actions';
import { CommunicationEntityType } from 'src/services/integration/integrations.types';
import ContactsNotesModal from './contacts-notes-modal';

type Props = {
  row: Contact
  selected?: boolean;
  onSelectRow?: VoidFunction;
  onRemoveContact?: (contact: Contact) => void;
  onSendEmail?: (contact: Contact) => void;
  onCreateCandidate?: (contact: Contact) => void;
};

export function ContactsTableRow({
  row,
  selected,
  onSelectRow,
  onRemoveContact,
  onCreateCandidate,
  onSendEmail
}: Props) {
  const { t } = useTranslate();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const tenantType = useOrgTenant();

  const dispatch = useAppDispatch();

  const showNotesModal = useBoolean();

  const { featureEnabled } = useFeatureFlags();

  const router = useRouter();

  const popover = usePopover();

  const { data: contactCompanyLinks } = useGetContactCompanyLinksQuery({
    contact_id: row.id,
    params: { page: 1, per_page: 1 },
  }, {
    skip: !!(row.company_name ?? row.account?.organization?.company_name),
  });

  const linkedCompanyName = row.company_name
    ?? row.account?.organization?.company_name
    ?? contactCompanyLinks?.results?.[0]?.company_profile?.company_name
    ?? '-';

  const viewDetail = () => {
    router.push(paths.dashboard.contacts.detail(row.id));
  }

  const confirm = useBoolean();

  const comms = useCommunicationActions(buildDialNumber(row.phone), `${row.first_name} ${row.last_name ?? ''}`.trim(), { entity_type: CommunicationEntityType.CONTACT, entity_id: row.id });

  const handleRemoveContact = () => {
    if (!onRemoveContact) return;

    confirm.onFalse();
    onRemoveContact(row);
  }

  const handleAddTask = useCallback(() => {
    dispatch(openTaskEditorSidesheet({
      contactId: row.id,
    }))
  }, [dispatch, row]);

  if (isMobile) {
    return (
      <Card sx={{ mx: 2, mb: 1 }}>
        <CardContent>
          <Stack direction="column" alignItems="start" mb={1}>
            <Typography variant="h6">
              {`${row.first_name} ${row.last_name ?? ''}`}
            </Typography>
            {!!row.position_title && (
              <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
                {row.position_title}
              </Typography>
            )}
            <Typography variant='caption' sx={{ color: theme.palette.text.primary }}>
              {linkedCompanyName}
            </Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            {row.email ?? '-'}
          </Typography>

          {
            (row.phone) && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {row.phone ?? '-'}
              </Typography>
            )
          }

          <Stack direction="row" justifyContent="flex-end" alignItems="center" mt={2} mb={2}>
            <IconButton
              sx={{ position: 'absolute', top: 15, right: 8, zIndex: 50 }}
              color={popover.open ? 'primary' : 'default'}
              onClick={popover.onOpen}
            >
              <Iconify icon="eva:more-horizontal-fill" />
            </IconButton>
          </Stack>
        </CardContent>

        <CustomPopover
          open={popover.open}
          onClose={popover.onClose}
          arrow="right-top"
          sx={{ width: 200 }}
        >
          <MenuItem
            onClick={() => {
              popover.onClose();
              viewDetail();
            }}
          >
            <Iconify icon="mdi:eye" />
            <Typography variant='body2'>{t('common.view')}</Typography>
          </MenuItem>


          <MenuItem
            onClick={showNotesModal.onTrue}
          >
            <Iconify icon="mdi:comments" />
            <Typography variant='body2'>{t('common.view_notes')}</Typography>
          </MenuItem>

          {
            (onCreateCandidate && tenantType !== TenantType.Candidate) && (
              <MenuItem
                onClick={() => {
                  popover.onClose();
                  onCreateCandidate(row);
                }}
              >
                <Iconify icon="hugeicons:student" />
                <Typography variant='body2'>{t('candidates.create_candidate')}</Typography>
              </MenuItem>
            )
          }

          {
            ((onSendEmail && featureEnabled(FeatureType.EMAIL_CONNECTOR_ENABLED)) && tenantType !== TenantType.Candidate) &&
            <MenuItem
              onClick={() => {
                popover.onClose();
                onSendEmail(row);
              }}
            >
              <Iconify icon="lsicon:email-send-filled" />
              <Typography variant='body2'>{t('common.send_email')}</Typography>
            </MenuItem>
          }

          {
            comms.commsActive && (
              <>
                <MenuItem onClick={() => { popover.onClose(); comms.call(); }}>
                  <Iconify icon="solar:phone-bold" />
                  <Typography variant='body2'>{t('integrations.communications.actions.call')}</Typography>
                </MenuItem>
                <MenuItem onClick={() => { popover.onClose(); comms.openSms(); }}>
                  <Iconify icon="solar:chat-round-dots-bold" />
                  <Typography variant='body2'>{t('integrations.communications.actions.sms')}</Typography>
                </MenuItem>
              </>
            )
          }

          {onRemoveContact && (
            <MenuItem
              color='error'
              onClick={confirm.onTrue}
            >
              <Iconify icon="eva:trash-2-outline" />
              <Typography variant='body2'>{t('common.remove')}</Typography>
            </MenuItem>
          )}

        </CustomPopover>

        <ContactsNotesModal
          threadId={row.thread.id}
          open={showNotesModal.value}
          onClose={showNotesModal.onFalse}
        />

        {onRemoveContact && (
          <ConfirmDialog
            open={confirm.value}
            onClose={confirm.onFalse}
            title={t('common.delete')}
            content={
              <Typography variant="body2">
                {t('contacts.delete_modal.confirm_message')}
              </Typography>
            }
            action={
              <Button variant="contained" color="error" onClick={handleRemoveContact}>
                {t('common.delete')}
              </Button>
            }
          />
        )}

        {comms.smsModal}
      </Card>
    );
  }

  return (
    <>
      <TableRow>

        {
          onSelectRow && (
            <TableCell padding="checkbox">
              <Checkbox checked={selected} onClick={onSelectRow} />
            </TableCell>
          )
        }

        <TableCell>
          <Stack direction="column" alignItems="start">
            <Link
              component={RouterLink}
              noWrap
              color="inherit"
              href={paths.dashboard.contacts.detail(row.id)}
            >
              <TruncatedTextField
                text={`${row.first_name} ${row.last_name ?? ''}`}
                mode="characters"
                limit={50}
                showMoreEnabled={false}
                variant="subtitle2"
                fontWeight="bold"
                style={{ textTransform: 'capitalize' }}
              />
            </Link>
            {!!row.position_title && (
              <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
                {row.position_title}
              </Typography>
            )}
          </Stack>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {linkedCompanyName}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {row.email ?? '-'}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {row.phone ?? '-'}
          </Typography>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="body2" color="text.secondary">
            {row.account_owner ? `${row.account_owner?.first_name} ${row.account_owner?.last_name}` : '--'}
          </Typography>
        </TableCell >

        <TableCell align="right" sx={{ px: 4 }}>
          <IconButton
            color={popover.open ? 'inherit' : 'default'}
            onClick={popover.onOpen}
          >
            <Iconify icon="eva:more-horizontal-fill" />
          </IconButton>
        </TableCell>

      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 200 }}
      >
        <MenuItem
          onClick={() => {
            popover.onClose();
            viewDetail();
          }}
        >
          <Iconify icon="mdi:eye" />
          <Typography variant='body2'>{t('common.view')}</Typography>
        </MenuItem>


        <MenuItem
          onClick={showNotesModal.onTrue}
        >
          <Iconify icon="mdi:comments" />
          <Typography variant='body2'>{t('common.notes')}</Typography>
        </MenuItem>

        {
          (onCreateCandidate && tenantType !== TenantType.Candidate) && (
            <MenuItem
              onClick={() => {
                popover.onClose();
                onCreateCandidate(row);
              }}
            >
              <Iconify icon="hugeicons:student" />
              <Typography variant='body2'>{t('candidates.create_candidate')}</Typography>
            </MenuItem>
          )
        }

        {
          ((onSendEmail && featureEnabled(FeatureType.EMAIL_CONNECTOR_ENABLED)) && tenantType !== TenantType.Candidate) &&
          <MenuItem
            onClick={() => {
              popover.onClose();
              onSendEmail(row);
            }}
          >
            <Iconify icon="lsicon:email-send-filled" />
            <Typography variant='body2'>{t('common.send_email')}</Typography>
          </MenuItem>
        }

        {
          comms.commsActive && (
            <>
              <MenuItem onClick={() => { popover.onClose(); comms.call(); }}>
                <Iconify icon="solar:phone-bold" />
                <Typography variant='body2'>{t('integrations.communications.actions.call')}</Typography>
              </MenuItem>
              <MenuItem onClick={() => { popover.onClose(); comms.openSms(); }}>
                <Iconify icon="solar:chat-round-dots-bold" />
                <Typography variant='body2'>{t('integrations.communications.actions.sms')}</Typography>
              </MenuItem>
            </>
          )
        }

        <MenuItem
          onClick={() => {
            popover.onClose();
            handleAddTask();
          }}
        >
          <Iconify icon="ic:round-add-task" />
          <Typography variant='body2'>{t('common.add_task')}</Typography>
        </MenuItem>

        {onRemoveContact && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={confirm.onTrue}
          >
            <Iconify icon="eva:trash-2-outline" />
            <Typography variant='body2'>{t('common.remove')}</Typography>
          </MenuItem>
        )}

      </CustomPopover>


      <ContactsNotesModal
        threadId={row.thread.id}
        open={showNotesModal.value}
        onClose={showNotesModal.onFalse}
      />

      {
        onRemoveContact && (
          <ConfirmDialog
            open={confirm.value}
            onClose={confirm.onFalse}
            title={t('common.delete')}
            content={
              <Typography variant="body2">
                {t('contacts.delete_modal.confirm_message')}
              </Typography>
            }
            action={
              <Button variant="contained" color="error" onClick={handleRemoveContact}>
                {t('common.delete')}
              </Button>
            }
          />
        )
      }

      {comms.smsModal}
    </>
  );
}
